import { Box } from "@mui/material";
import { useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../../context/AuthContext";
import {
  useUsers,
  useCreateUser,
  useEditUser,
  useDeleteUser,
  useAdminResetPassword,
} from "../../../hooks/useUser";
import { useGetRoles } from "../../../hooks/useRole";
import type User from "../../../types/user/User";
import type Role from "../../../types/role/Role";
import {
  toolbarBtnSx,
  flyoutInputSx,
  flyoutLabelSx,
  crudTableSx,
  selectedRowSx,
  radioInputSx,
  primaryCellSx,
  confirmCancelBtnSx,
  confirmDeleteBtnSx,
  CrudPageLayout,
  LoadingState,
  EmptyState,
  FlyoutDrawer,
  FlyoutFooter,
  flyoutLabels,
  formErrorSx,
  DeleteConfirmation,
  PlusIcon,
} from "../../components/shared";

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function V2Users() { // NOSONAR
  const { t } = useTranslation(["users", "common", "errors", "grid"]);
  const { customerId } = useParams<{ customerId: string }>();
  const { user: authUser } = useContext(AuthContext);
  // -- Data -----------------------------------------------------------------
  const { data: usersData, isLoading: usersLoading } = useUsers(customerId);
  const { data: roles } = useGetRoles(customerId);
  const users = usersData?.users ?? [];
  const rolesMap = useMemo(() => {
    const m = new Map<string, Role>();
    if (roles) {
      for (const r of roles as Role[]) {
        m.set(r.roleId, r);
      }
    }
    return m;
  }, [roles]);

  // -- Mutations ------------------------------------------------------------
  const createUserMutation = useCreateUser(customerId ?? "");
  const editUserMutation = useEditUser(customerId ?? "");
  const deleteUserMutation = useDeleteUser(customerId ?? "");
  const resetPasswordMutation = useAdminResetPassword();

  // -- Selection ------------------------------------------------------------
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const selectedUser = users.find((u) => u.userId === selectedUserId) ?? null;

  // -- Flyout state ---------------------------------------------------------
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const [flyoutMode, setFlyoutMode] = useState<"create" | "edit">("create");

  // -- Flyout form state ----------------------------------------------------
  const [formEmail, setFormEmail] = useState("");
  const [formFirstName, setFormFirstName] = useState("");
  const [formLastName, setFormLastName] = useState("");
  const [formRoleId, setFormRoleId] = useState("");
  const [formPassword, setFormPassword] = useState("");
  const [formConfirmPassword, setFormConfirmPassword] = useState("");
  const [formError, setFormError] = useState("");

  // -- Delete confirmation state --------------------------------------------
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // -- Reset password state -------------------------------------------------
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetTempPassword, setResetTempPassword] = useState("");
  const [resetError, setResetError] = useState("");

  // -- Permissions ----------------------------------------------------------
  const canCreate = authUser?.hasPermission("users:Create") ?? false;
  const canEdit = authUser?.hasPermission("users:Update") ?? false;
  const canDelete = authUser?.hasPermission("users:Delete") ?? false;
  const canResetPassword =
    authUser?.hasPermission("users:*") ||
    authUser?.scope.customer === "*";

  // -- Reset form -----------------------------------------------------------
  const resetForm = () => {
    setFormEmail("");
    setFormFirstName("");
    setFormLastName("");
    setFormRoleId("");
    setFormPassword("");
    setFormConfirmPassword("");
    setFormError("");
    setShowDeleteConfirm(false);
    setShowResetPassword(false);
    setResetTempPassword("");
    setResetError("");
  };

  // -- Populate form from user ----------------------------------------------
  const populateFromUser = (user: User) => {
    setFormFirstName(user.firstName);
    setFormLastName(user.lastName);
    setFormRoleId(user.roleId);
    setFormEmail("");
    setFormPassword("");
    setFormConfirmPassword("");
    setFormError("");
    setShowDeleteConfirm(false);
    setShowResetPassword(false);
    setResetTempPassword("");
    setResetError("");
  };

  // -- Handlers -------------------------------------------------------------
  const handleRowSelect = (userId: string) => {
    if (!canEdit) {
      setSelectedUserId((prev) => (prev === userId ? null : userId));
      return;
    }
    const user = users.find((u) => u.userId === userId);
    if (!user) return;

    if (selectedUserId === userId && flyoutOpen) {
      // Clicking same row while flyout open — close it
      setFlyoutOpen(false);
      setSelectedUserId(null);
      return;
    }

    setSelectedUserId(userId);
    setFlyoutMode("edit");
    setFlyoutOpen(true);
    populateFromUser(user);
  };

  // When selectedUser changes while flyout is open in edit mode, repopulate
  useEffect(() => {
    if (flyoutOpen && flyoutMode === "edit" && selectedUser) {
      populateFromUser(selectedUser);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser?.userId]);

  const handleCreate = () => {
    setSelectedUserId(null);
    setFlyoutMode("create");
    setFlyoutOpen(true);
    resetForm();
  };

  const handleCloseFlyout = () => {
    setFlyoutOpen(false);
    resetForm();
  };

  const handleSave = () => {
    if (flyoutMode === "create") {
      if (!formEmail.trim() || !formFirstName.trim() || !formLastName.trim() || !formRoleId || !formPassword) {
        setFormError(t("common:allFieldsRequired"));
        return;
      }
      if (formPassword !== formConfirmPassword) {
        setFormError(t("common:passwordsDoNotMatch"));
        return;
      }
      setFormError("");
      createUserMutation.mutate(
        { firstName: formFirstName.trim(), lastName: formLastName.trim(), email: formEmail.trim(), roleId: formRoleId, password: formPassword },
        {
          onSuccess: () => {
            setFlyoutOpen(false);
            resetForm();
          },
          onError: (err) => setFormError(err.message || t("users:failedToCreateUser")),
        },
      );
    } else {
      if (!selectedUser) return;
      if (!formFirstName.trim() || !formLastName.trim() || !formRoleId) {
        setFormError(t("common:allFieldsRequired"));
        return;
      }
      setFormError("");
      editUserMutation.mutate(
        { userId: selectedUser.userId, firstName: formFirstName.trim(), lastName: formLastName.trim(), roleId: formRoleId },
        {
          onSuccess: () => {
            setFlyoutOpen(false);
            resetForm();
          },
          onError: (err) => setFormError(err.message || t("users:failedToUpdateUser")),
        },
      );
    }
  };

  const handleDelete = () => {
    if (!selectedUser) return;
    deleteUserMutation.mutate(
      { userId: selectedUser.userId, username: selectedUser.email },
      {
        onSuccess: () => {
          setFlyoutOpen(false);
          setSelectedUserId(null);
          resetForm();
        },
        onError: () => {},
      },
    );
  };

  const handleResetPassword = () => {
    if (!selectedUser || !customerId) return;
    if (!resetTempPassword) {
      setResetError(t("users:temporaryPasswordRequired"));
      return;
    }
    setResetError("");
    resetPasswordMutation.mutate(
      { customerId, userId: selectedUser.userId, temporaryPassword: resetTempPassword },
      {
        onSuccess: () => {
          setShowResetPassword(false);
          setResetTempPassword("");
          setResetError("");
        },
        onError: (err) => setResetError(err.message || t("users:failedToResetPassword")),
      },
    );
  };

  const isSaving = createUserMutation.isPending || editUserMutation.isPending;

  // -- Render ---------------------------------------------------------------

  const toolbarContent = canCreate ? (
    <Box component="button" onClick={handleCreate} sx={toolbarBtnSx(false)}>
      <PlusIcon />
      {t("users:createUser")}
    </Box>
  ) : null;

  const tableContent = usersLoading ? (
    <LoadingState message={t("users:loadingUsers")} />
  ) : users.length === 0 ? (
    <EmptyState message={t("users:noUsersFound")} />
  ) : (
    <Box component="table" sx={crudTableSx}>
      <thead>
        <tr>
          <Box component="th" sx={{ width: 40, textAlign: "center" }}>
            {/* Radio header — intentionally blank */}
          </Box>
          <th>{t("grid:fullName")}</th>
          <th>{t("grid:email")}</th>
          <th>{t("users:role")}</th>
          <th>{t("users:authType")}</th>
        </tr>
      </thead>
      <tbody>
        {users.map((u: User) => {
          const isSelected = selectedUserId === u.userId;
          const roleName = rolesMap.get(u.roleId)?.name ?? "\u2014";
          const fullName = `${u.firstName} ${u.lastName}`;

          return (
            <Box
              component="tr"
              key={u.userId}
              onClick={() => handleRowSelect(u.userId)}
              sx={selectedRowSx(isSelected)}
            >
              {/* Radio */}
              <Box component="td" sx={{ textAlign: "center" }}>
                <Box
                  component="input"
                  type="radio"
                  name="user-select"
                  checked={isSelected}
                  onChange={() => handleRowSelect(u.userId)}
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  sx={radioInputSx}
                />
              </Box>

              {/* Full Name */}
              <Box component="td" sx={primaryCellSx}>
                {fullName}
              </Box>

              {/* Email */}
              <td>{u.email}</td>

              {/* Role */}
              <td>{roleName}</td>

              {/* Auth Type */}
              <td>
                <Box
                  component="span"
                  sx={{
                    display: "inline-block",
                    px: "8px",
                    py: "2px",
                    borderRadius: "4px",
                    fontSize: "0.6875rem",
                    fontWeight: 600,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    backgroundColor: u.ssoUser
                      ? "var(--accent-dim)"
                      : "var(--border-mid)",
                    color: u.ssoUser
                      ? "var(--accent)"
                      : "var(--text-secondary)",
                  }}
                >
                  {u.ssoUser ? t("users:sso") : t("users:standard")}
                </Box>
              </td>
            </Box>
          );
        })}
      </tbody>
    </Box>
  );

  const { flyoutTitle, saveLabel } = flyoutLabels(flyoutMode, isSaving, {
    createTitle: t("users:createUser"), editTitle: t("users:editUser"),
    creating: t("users:creatingUser"), saving: t("users:savingUser"), saveChanges: t("common:saveChanges"),
  });

  return (
    <CrudPageLayout
      toolbar={toolbarContent}
      extra={
        <FlyoutDrawer
          open={flyoutOpen}
          onClose={handleCloseFlyout}
          title={flyoutTitle}
          footer={
            <FlyoutFooter
              onCancel={handleCloseFlyout}
              cancelLabel={t("common:cancel")}
              onSave={handleSave}
              saveLabel={saveLabel}
              isSaving={isSaving}
              showDelete={flyoutMode === "edit" && canDelete && !showDeleteConfirm}
              onDelete={() => setShowDeleteConfirm(true)}
              deleteLabel={t("users:deleteUser")}
            />
          }
        >
          {formError && (
            <Box sx={formErrorSx}>{formError}</Box>
          )}

          {/* Create mode: Email field */}
          {flyoutMode === "create" && (
            <Box>
              <Box sx={flyoutLabelSx}>{t("users:email")}</Box>
              <Box
                component="input"
                type="email"
                placeholder="user@example.com"
                value={formEmail}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormEmail(e.target.value)}
                sx={flyoutInputSx}
              />
            </Box>
          )}

          {/* Edit mode: Show email read-only */}
          {flyoutMode === "edit" && selectedUser && (
            <Box>
              <Box sx={flyoutLabelSx}>{t("users:email")}</Box>
              <Box
                sx={{
                  fontSize: "0.875rem",
                  color: "var(--text-secondary)",
                  py: "8px",
                }}
              >
                {selectedUser.email}
              </Box>
            </Box>
          )}

          {/* Name fields */}
          <Box sx={{ display: "flex", gap: "12px" }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={flyoutLabelSx}>{t("users:firstName")}</Box>
              <Box
                component="input"
                type="text"
                placeholder={t("users:firstName")}
                value={formFirstName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormFirstName(e.target.value)}
                sx={flyoutInputSx}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Box sx={flyoutLabelSx}>{t("users:lastName")}</Box>
              <Box
                component="input"
                type="text"
                placeholder={t("users:lastName")}
                value={formLastName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormLastName(e.target.value)}
                sx={flyoutInputSx}
              />
            </Box>
          </Box>

          {/* Role dropdown */}
          <Box>
            <Box sx={flyoutLabelSx}>{t("users:role")}</Box>
            <Box
              component="select"
              value={formRoleId}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFormRoleId(e.target.value)}
              sx={{
                ...flyoutInputSx,
                appearance: "auto" as const,
              }}
            >
              <option value="">{t("common:selectRole")}</option>
              {((roles as Role[] | undefined) ?? []).map((r) => (
                <option key={r.roleId} value={r.roleId}>
                  {r.name}
                </option>
              ))}
            </Box>
          </Box>

          {/* Create mode: Password fields */}
          {flyoutMode === "create" && (
            <Box sx={{ display: "flex", gap: "12px" }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={flyoutLabelSx}>{t("users:temporaryPassword")}</Box>
                <Box
                  component="input"
                  type="password"
                  placeholder={t("users:password")}
                  value={formPassword}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormPassword(e.target.value)}
                  sx={flyoutInputSx}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Box sx={flyoutLabelSx}>{t("users:confirmPassword")}</Box>
                <Box
                  component="input"
                  type="password"
                  placeholder={t("users:confirmPasswordPlaceholder")}
                  value={formConfirmPassword}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormConfirmPassword(e.target.value)}
                  sx={flyoutInputSx}
                />
              </Box>
            </Box>
          )}

          {/* Edit mode: Reset Password section (non-SSO only) */}
          {flyoutMode === "edit" && selectedUser && !selectedUser.ssoUser && canResetPassword && (
            <Box
              sx={{
                borderTop: "1px solid var(--border-subtle)",
                pt: "16px",
                mt: "4px",
              }}
            >
              {showResetPassword ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "10px",
                      p: "12px",
                      borderRadius: "8px",
                      backgroundColor: "rgba(251,113,133,0.08)",
                      border: "1px solid rgba(251,113,133,0.2)",
                    }}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="var(--signal-rose)" strokeWidth={2} style={{ width: 16, height: 16, marginTop: 2, flexShrink: 0 }}>
                      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                      <line x1="12" y1="9" x2="12" y2="13" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                    <Box sx={{ color: "var(--signal-rose)", fontSize: "0.8125rem", lineHeight: 1.5 }}>
                      {t("users:resetPasswordWarning")}
                    </Box>
                  </Box>

                  {resetError && (
                    <Box sx={{ color: "var(--signal-rose)", fontSize: "0.8125rem" }}>{resetError}</Box>
                  )}

                  <Box>
                    <Box sx={flyoutLabelSx}>{t("users:temporaryPassword")}</Box>
                    <Box
                      component="input"
                      type="password"
                      placeholder={t("users:enterTemporaryPassword")}
                      value={resetTempPassword}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setResetTempPassword(e.target.value)}
                      sx={flyoutInputSx}
                    />
                  </Box>

                  <Box sx={{ display: "flex", gap: "8px" }}>
                    <Box
                      component="button"
                      onClick={() => {
                        setShowResetPassword(false);
                        setResetTempPassword("");
                        setResetError("");
                      }}
                      sx={confirmCancelBtnSx}
                    >
                      {t("common:cancel")}
                    </Box>
                    <Box
                      component="button"
                      onClick={handleResetPassword}
                      disabled={resetPasswordMutation.isPending}
                      sx={confirmDeleteBtnSx}
                    >
                      {resetPasswordMutation.isPending ? t("users:resetting") : t("users:resetPassword")}
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box
                  component="button"
                  onClick={() => setShowResetPassword(true)}
                  sx={toolbarBtnSx(false)}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} style={{ width: 14, height: 14 }}>
                    <rect x="3" y="11" width="18" height="11" rx="2" />
                    <path d="M7 11V7a5 5 0 0110 0v4" />
                  </svg>
                  {t("users:resetPassword")}
                </Box>
              )}
            </Box>
          )}

          {/* Edit mode: Delete confirmation */}
          {flyoutMode === "edit" && showDeleteConfirm && selectedUser && (
            <DeleteConfirmation
              message={t("users:deleteUserConfirm", { name: `${selectedUser.firstName} ${selectedUser.lastName}` })}
              error={deleteUserMutation.error?.message}
              onCancel={() => setShowDeleteConfirm(false)}
              cancelLabel={t("common:cancel")}
              onConfirm={handleDelete}
              confirmLabel={t("common:confirmDeleteTitle")}
              isPending={deleteUserMutation.isPending}
              pendingLabel={t("common:deleting")}
            />
          )}
        </FlyoutDrawer>
      }
    >
      {tableContent}
    </CrudPageLayout>
  );
}
